import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Loading } from "@/components/States";
import { toast } from "sonner";

const STEPS = [
  { key: "name", label: "What's your garage called?", placeholder: "Brixton Garage" },
  { key: "vat_number", label: "Your VAT number (optional)", placeholder: "GB123456789" },
  { key: "default_hourly_rate", label: "Default labour rate (£/hr)", placeholder: "65", type: "number" },
  { key: "invoice_prefix", label: "Invoice prefix", placeholder: "BG-" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api("/garage/settings").then((g) => { setValues({ name: g.name || "", vat_number: g.vat_number || "", default_hourly_rate: g.default_hourly_rate ?? "", invoice_prefix: g.invoice_prefix || "" }); }).finally(() => setLoading(false)); }, []);

  const cur = STEPS[step];
  const next = async () => {
    if (step < STEPS.length - 1) { setStep(step + 1); return; }
    setSaving(true);
    try {
      await api("/garage/settings", { method: "PATCH", body: JSON.stringify({
        name: values.name || null, vat_number: values.vat_number || null,
        default_hourly_rate: values.default_hourly_rate ? Number(values.default_hourly_rate) : null,
        invoice_prefix: values.invoice_prefix || null,
      }) });
      await api("/garage/onboarding/complete", { method: "POST" });
      toast.success("Setup complete!");
      navigate("/dashboard");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (loading) return <Loading />;
  return (
    <div className="mx-auto max-w-lg pt-8">
      <div className="mb-8 flex items-center gap-2">
        {STEPS.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-brand" : "bg-secondary"}`} />)}
      </div>
      <p className="mb-1 text-[11px] font-extrabold tracking-widest text-brand">STEP {step + 1} OF {STEPS.length}</p>
      <h1 className="font-display text-3xl font-bold uppercase text-midnight">{cur.label}</h1>
      <input autoFocus type={cur.type || "text"} value={values[cur.key] || ""} onChange={(e) => setValues({ ...values, [cur.key]: e.target.value })}
        onKeyDown={(e) => e.key === "Enter" && next()} placeholder={cur.placeholder} data-testid={`onboarding-${cur.key}`}
        className="mt-6 w-full rounded-lg border border-input bg-card px-4 py-4 text-lg outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
      <div className="mt-6 flex items-center justify-between">
        {step > 0 ? <button onClick={() => setStep(step - 1)} className="text-sm font-semibold text-muted-foreground">Back</button> : <span />}
        <button onClick={next} disabled={saving} data-testid="onboarding-next" className="inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : step === STEPS.length - 1 ? <><Check className="h-4 w-4" />FINISH</> : <>NEXT <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    </div>
  );
}
